import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import axios from 'axios';
import { Bell } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

// In-app notification bell: polls unread count and lists recent notifications.
// A notification is marked read when CLICKED (not when the popover opens), so an
// important one isn't silently wiped by glancing at the bell; 全部標為已讀 is explicit.
const NotificationBell = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const fetchNotifs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications`, { withCredentials: true });
      setItems(res.data.items || []);
      setUnread(res.data.unread || 0);
    } catch (e) {
      /* silent */
    }
  }, []);

  useEffect(() => {
    fetchNotifs();
    const id = setInterval(fetchNotifs, 30000);
    return () => clearInterval(id);
  }, [fetchNotifs]);

  const go = (n) => {
    setOpen(false);
    if (!n.read) {
      axios.post(`${API}/notifications/${n.notification_id}/read`, {}, { withCredentials: true }).catch(() => {});
      setItems((prev) => prev.map((x) => (x.notification_id === n.notification_id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
    }
    if (n.link) navigate(n.link);
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read`, {}, { withCredentials: true });
    } catch (e) {
      /* silent */
    }
    setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    setUnread(0);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative p-1.5 rounded-sm hover:bg-[#F5F0E6] text-[#5C5C5C]" data-testid="notif-bell" aria-label="通知">
          <Bell className="w-5 h-5" strokeWidth={1.5} />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-[#C93B2B] text-white text-[10px] rounded-full flex items-center justify-center"
              data-testid="notif-count"
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 rounded-sm p-0 max-h-[70vh] overflow-y-auto">
        <div className="p-3 border-b border-[#E5E0D8] flex items-center justify-between">
          <p className="text-sm font-medium text-[#1A1A1A]">通知</p>
          {unread > 0 && (
            <button onClick={markAllRead} className="text-xs text-[#C93B2B] hover:underline" data-testid="mark-all-read">
              全部標為已讀
            </button>
          )}
        </div>
        {items.length === 0 ? (
          <p className="text-sm text-[#5C5C5C] p-4 text-center">暫無通知</p>
        ) : (
          items.map((n) => (
            <button
              key={n.notification_id}
              onClick={() => go(n)}
              className={`block w-full text-left px-3 py-2.5 border-b border-[#F0EBE0] hover:bg-[#F5F0E6] ${n.read ? '' : 'bg-[#FEF8F0]'}`}
              data-testid={`notif-${n.notification_id}`}
            >
              <p className="text-sm text-[#1A1A1A]">{n.title}</p>
              <p className="text-xs text-[#9A9A9A] mt-0.5">{new Date(n.created_at).toLocaleString('zh-TW')}</p>
            </button>
          ))
        )}
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
